import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Send, ExternalLink, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../components/ui/use-toast';

const GOOGLE_MAPS_SHARE_URL = 'https://maps.app.goo.gl/hr8rW1GEFQpnKiT38';
const GOOGLE_MAPS_PLACE_NAME = 'TRADEZONE EQUIPEMENTS';
const GOOGLE_MAPS_LAT = 22.5676542;
const GOOGLE_MAPS_LNG = 88.3540439;
const GOOGLE_MAPS_EMBED_URL = `https://maps.google.com/maps?q=${encodeURIComponent(
  `${GOOGLE_MAPS_PLACE_NAME}@${GOOGLE_MAPS_LAT},${GOOGLE_MAPS_LNG}`,
)}&ll=${GOOGLE_MAPS_LAT},${GOOGLE_MAPS_LNG}&z=17&hl=en&output=embed`;

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    inquiryType: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const inquiryTypes = [
    'Product Inquiry',
    'Price Quote',
    'Technical Support',
    'Partnership',
    'General Question'
  ];

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Address',
      content: 'SECOND FLOOR, 8/1, Princep St, opp. IFA OFFICE, Esplanade, Chowringhee North, Bow Barracks, Kolkata, West Bengal 700001'
    },
    {
      icon: Phone,
      title: 'Phone',
      content: '+91 98756 30348'
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      content: '+91 98756 30348',
      href: 'https://wa.me/919875630348'
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'info@tradezoneequipments.com'
    }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    }

    if (!formData.company.trim()) {
      newErrors.company = 'Company is required';
    }

    if (!formData.inquiryType) {
      newErrors.inquiryType = 'Please select an inquiry type';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields correctly.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      toast({
        title: "Message Sent Successfully!",
        description: "Thank you for your inquiry. Our team will contact you within 24 hours.",
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        inquiryType: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1500);
  };

  return (
    <section id="contact" className="py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-[#1a1a1a] to-[#2d2d2d]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 lg:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Get In Touch
          </h2>
          <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto">
            Have questions about our products? We're here to help you find the perfect parts for your needs.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div>
              <h3 className="text-2xl font-bold text-white mb-6">Contact Information</h3>
              <p className="text-gray-400 mb-8">
                Reach out to our team for any inquiries about our products, services, or partnership opportunities.
              </p>
            </div>

            {contactInfo.map((info, index) => {
              const IconComponent = info.icon;
              const Wrapper = info.href ? motion.a : motion.div;
              const wrapperProps = info.href
                ? { href: info.href, target: '_blank', rel: 'noopener noreferrer' }
                : {};
              return (
                <Wrapper
                  key={index}
                  {...wrapperProps}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + index * 0.1, duration: 0.5 }}
                  className="flex items-start space-x-4 p-4 bg-[#2d2d2d] rounded-lg hover:bg-[#4a4a4a]/30 transition-colors duration-300"
                >
                  <div className="w-12 h-12 bg-[#ff6b35]/20 rounded-lg flex items-center justify-center shrink-0">
                    <IconComponent size={24} className="text-[#ff6b35]" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">{info.title}</h4>
                    <p className="text-gray-400">{info.content}</p>
                  </div>
                </Wrapper>
              );
            })}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="bg-[#2d2d2d] rounded-xl overflow-hidden border border-[#4a4a4a]/30"
              aria-labelledby="contact-map-heading"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-[#4a4a4a]/30">
                <div className="flex items-center gap-2">
                  <MapPin size={18} className="text-[#ff6b35]" aria-hidden="true" />
                  <h4 id="contact-map-heading" className="text-white font-semibold">
                    Find us on the map
                  </h4>
                </div>
                <a
                  href={GOOGLE_MAPS_SHARE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-[#ff6b35] hover:text-[#ff8c42] transition-colors"
                >
                  Open
                  <ExternalLink size={14} aria-hidden="true" />
                </a>
              </div>
              <div className="relative w-full" style={{ aspectRatio: '4 / 3' }}>
                <iframe
                  title="Trade Zone Equipments location on Google Maps"
                  src={GOOGLE_MAPS_EMBED_URL}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full border-0"
                />
              </div>
            </motion.div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <form onSubmit={handleSubmit} className="bg-[#2d2d2d] p-8 rounded-xl space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white font-medium mb-2">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full bg-[#1a1a1a] text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff6b35] transition-all duration-300 ${
                      errors.name ? 'ring-2 ring-red-500' : ''
                    }`}
                    placeholder="Your Name"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full bg-[#1a1a1a] text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff6b35] transition-all duration-300 ${
                      errors.email ? 'ring-2 ring-red-500' : ''
                    }`}
                    placeholder="Your Email"
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white font-medium mb-2">Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full bg-[#1a1a1a] text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff6b35] transition-all duration-300 ${
                      errors.phone ? 'ring-2 ring-red-500' : ''
                    }`}
                    placeholder=""
                  />
                  {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Company *</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className={`w-full bg-[#1a1a1a] text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff6b35] transition-all duration-300 ${
                      errors.company ? 'ring-2 ring-red-500' : ''
                    }`}
                    placeholder="Your Company"
                  />
                  {errors.company && <p className="text-red-500 text-sm mt-1">{errors.company}</p>}
                </div>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Inquiry Type *</label>
                <select
                  name="inquiryType"
                  value={formData.inquiryType}
                  onChange={handleChange}
                  className={`w-full bg-[#1a1a1a] text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff6b35] transition-all duration-300 ${
                    errors.inquiryType ? 'ring-2 ring-red-500' : ''
                  }`}
                >
                  <option value="">Select an inquiry type</option>
                  {inquiryTypes.map((type, index) => (
                    <option key={index} value={type}>{type}</option>
                  ))}
                </select>
                {errors.inquiryType && <p className="text-red-500 text-sm mt-1">{errors.inquiryType}</p>}
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="5"
                  className={`w-full bg-[#1a1a1a] text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff6b35] transition-all duration-300 resize-none ${
                    errors.message ? 'ring-2 ring-red-500' : ''
                  }`}
                  placeholder="Tell us about your requirements..."
                />
                {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#ff6b35] hover:bg-[#ff8c42] text-white py-3 rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-[#ff6b35]/30 disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    Send Message
                    <Send className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={18} />
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;